import { useCallback, useEffect, useRef, useState } from 'react';
import type { DashboardPayload, DashboardPeriod } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * How often the dashboard re-reads `GET /dashboard`.
 *
 * Polling, not a socket: `socket.io-client` is not a dependency of this app and
 * the server emits no dashboard events, so the board follows `useDevices` and
 * re-reads on a timer. 30s rather than the device board's 10s because this
 * endpoint aggregates a whole month of rows — the occupancy tile is the only
 * genuinely live figure here, and the Device List already refreshes that faster
 * for the people who watch it all shift.
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Shared's payload plus the venue's service-charge rate — the API's
 * `DashboardOverviewDto`, carried locally for the same frozen-package reason
 * as the box screen's audit DTOs. Optional so a server from before the field
 * reads as "no charge configured"; 0 means the venue charges none.
 */
export interface DashboardOverview extends DashboardPayload {
  serviceChargePercent?: number;
}

export interface DashboardState {
  data: DashboardOverview | null;
  loading: boolean;
  /** API-supplied detail, shown beneath a translated headline. Null when healthy. */
  error: string | null;
  reload: (options?: { quiet?: boolean }) => Promise<void>;
}

/**
 * The whole screen in one request (SPEC §5.1).
 *
 * The payload is rendered exactly as the server computed it — no arithmetic
 * happens in the components. `@gc/shared`'s dashboard module already guarantees
 * real zeroes rather than `NaN` for a venue with no trade, so there is nothing
 * for the UI to defend against and no reason for it to re-derive a figure the
 * Overall Report derives elsewhere.
 *
 * A failed fetch keeps whatever payload was last good and raises `error`
 * alongside it: a poll that fails at 03:00 should not blank a screen that was
 * correct a moment ago.
 */
export function useDashboard(period: DashboardPeriod): DashboardState {
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Keeps the latest period available to the interval without re-creating it.
  const periodRef = useRef(period);
  periodRef.current = period;

  const load = useCallback(async (options: { quiet?: boolean } = {}) => {
    if (!options.quiet) setLoading(true);
    try {
      const response = await api.get<DashboardOverview>(
        `/dashboard?period=${encodeURIComponent(periodRef.current)}`,
      );
      setData(response);
      setError(null);
    } catch (caught) {
      // The message is the server's; the page pairs it with a translated
      // headline rather than showing a bare "Not Found" to an owner.
      setError(caught instanceof ApiError ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, period]);

  useEffect(() => {
    // `quiet` so the screen does not flash a spinner every 30 seconds.
    const timer = setInterval(() => void load({ quiet: true }), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return { data, loading, error, reload: load };
}
